import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserPlus, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

/**
 * PeopleToFollowCard — right-rail card with smart-match member
 * recommendations. Users can follow inline; already-followed members
 * are excluded. Uses /api/members/smart-match ranked by profile overlap.
 */
export default function PeopleToFollowCard({ currentUserId, limit = 4, testid = 'people-to-follow' }) {
  const [items, setItems] = useState([]);
  const [followingIds, setFollowingIds] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`${BACKEND_URL}/api/members/smart-match`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(`${BACKEND_URL}/api/members/me/following`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
    ]).then(([matches, following]) => {
      if (cancelled) return;
      // /api/members/me/following returns an array of user_id strings.
      const followed = Array.isArray(following)
        ? following.map(f => typeof f === 'string' ? f : (f.user_id || f.following_id)).filter(Boolean)
        : [];
      setFollowingIds(followed);
      const list = Array.isArray(matches) ? matches : [];
      // Exclude already-followed + own id
      const filtered = list.filter(m => {
        const uid = m.user?.user_id;
        return uid && uid !== currentUserId && !followed.includes(uid);
      });
      setItems(filtered.slice(0, limit));
    }).catch(() => {}).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [currentUserId, limit]);

  const handleFollow = async (userId) => {
    setBusyId(userId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/members/${userId}/follow`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error();
      setFollowingIds(prev => [...prev, userId]);
      toast.success('Following');
    } catch {
      toast.error('Could not follow');
    }
    setBusyId(null);
  };

  if (!loading && items.length === 0) return null;

  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-brand-navy" />
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">People to Follow</h3>
        </div>
        <Link to="/members"
          className="text-[11px] text-brand-navy hover:underline inline-flex items-center gap-0.5"
          data-testid={`${testid}-view-all`}>
          View all <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="flex gap-2 items-center animate-pulse">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-2.5 bg-slate-100 rounded w-3/4" />
                <div className="h-2 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map(m => {
            const u = m.user || {};
            const isFollowing = followingIds.includes(u.user_id);
            const role = u.profile?.role || u.role || '';
            const org = u.profile?.organisation || '';
            return (
              <div
                key={u.user_id}
                className="flex gap-2 items-center"
                data-testid={`${testid}-item-${u.user_id}`}
              >
                <Link to={`/profile/public/${u.user_id}`} className="flex-shrink-0">
                  <img
                    src={`/api/members/${u.user_id}/avatar`}
                    alt={u.name}
                    className="w-8 h-8 rounded-full object-cover bg-slate-100"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                </Link>
                <Link to={`/profile/public/${u.user_id}`} className="flex-1 min-w-0 group">
                  <p className="text-xs font-semibold text-slate-800 truncate group-hover:text-brand-navy transition-colors">
                    {u.name}
                  </p>
                  <p className="text-[10px] text-slate-400 truncate">
                    {role}{role && org ? ' · ' : ''}{org}
                  </p>
                </Link>
                {isFollowing ? (
                  <span className="text-[10px] text-brand-sage inline-flex items-center gap-0.5 flex-shrink-0">
                    <Check className="h-3 w-3" /> Following
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[10px] flex-shrink-0"
                    disabled={busyId === u.user_id}
                    onClick={() => handleFollow(u.user_id)}
                    data-testid={`${testid}-follow-${u.user_id}`}
                  >
                    {busyId === u.user_id ? '…' : 'Follow'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
